import { Link } from '@tanstack/react-router';
import { Archive, Edit3, RotateCcw } from 'lucide-react';
import {
  DashboardDataTableColumnKind,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
} from '@shigo-ui/dashboard-ui';

import { dashboardT } from '../../../../../lib/messages';
import { DealStatus, type Deal } from '../../model';
import {
  createDealAuctionTypeColumn,
  createDealBidFloorColumn,
  createDealFlightDatesColumn,
  createDealIdColumn,
  createDealIntegrationsColumn,
} from '../DealSummaryColumns';

interface DealTableColumnOptions {
  canEdit: boolean;
  canArchive: boolean;
  pendingIds: Set<string>;
  onStatusChange: (deal: Deal) => void;
}

export function createDealsTableColumns(options: DealTableColumnOptions) {
  return buildDashboardDataTableColumns<Deal>([
    {
      id: 'name',
      kind: DashboardDataTableColumnKind.Text,
      header: 'Name',
      headerClassName: 'w-72 pl-4',
      className: 'w-72 pl-4 font-medium',
      sortable: true,
      sortKey: 'name',
      value: (deal) => (
        <Link
          className={dashboardTableTextActionClassName()}
          to="/demand/deals/edit/$dealId"
          params={{ dealId: deal.id }}
        >
          {deal.name || deal.id}
        </Link>
      ),
    },
    createDealIdColumn(),
    createDealAuctionTypeColumn(),
    createDealFlightDatesColumn(),
    createDealBidFloorColumn(),
    createDealIntegrationsColumn(),
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      hidden: !options.canEdit && !options.canArchive,
      header: 'Actions',
      cell: (deal) => (
        <DashboardTableRowActions
          label={dashboardT('deals.actionsFor', { name: deal.name || 'deal' })}
        >
          {options.canEdit ? (
            <DashboardTableRowActionItem asChild>
              <Link to="/demand/deals/edit/$dealId" params={{ dealId: deal.id }}>
                <Edit3 className="h-4 w-4" aria-hidden="true" />
                Edit
              </Link>
            </DashboardTableRowActionItem>
          ) : null}
          {options.canArchive ? (
            <DashboardTableRowActionItem
              disabled={options.pendingIds.has(deal.id)}
              onSelect={() => {
                options.onStatusChange(deal);
              }}
            >
              {deal.status === DealStatus.Archived ? (
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Archive className="h-4 w-4" aria-hidden="true" />
              )}
              {deal.status === DealStatus.Archived ? 'Restore' : 'Archive'}
            </DashboardTableRowActionItem>
          ) : null}
        </DashboardTableRowActions>
      ),
    },
  ]);
}
